import React from 'react';
import { useTranslation } from 'react-i18next';
import './Skeleton.css';

/**
 * Shared shimmer skeleton primitives (ACT-UI-F global loading system): a
 * moving highlight sweeps across content-shaped gray placeholders, the way
 * YouTube/Facebook-style loading states work, instead of a generic spinner
 * or blank region. Reserved for INITIAL loads of content whose shape is
 * already known - never for action submission, live progress, file-transfer
 * progress, or button-level operations (those keep their existing spinners).
 */

export interface SkeletonBlockProps {
  width?: number | string;
  height?: number | string;
  circle?: boolean;
  borderRadius?: number | string;
  className?: string;
  style?: React.CSSProperties;
}

export const SkeletonBlock: React.FC<SkeletonBlockProps> = ({
  width = '100%',
  height = '0.85rem',
  circle = false,
  borderRadius,
  className,
  style,
}) => (
  <span
    aria-hidden="true"
    className={`dms-skeleton-block${circle ? ' dms-skeleton-block--circle' : ''}${className ? ` ${className}` : ''}`}
    style={{
      width: circle ? height : width,
      height,
      borderRadius: circle ? undefined : borderRadius,
      ...style,
    }}
  />
);

interface SkeletonRegionProps {
  children: React.ReactNode;
  label?: string;
  className?: string;
}

/** Wraps a group of skeleton pieces with the accessible busy/status contract. */
export const SkeletonRegion: React.FC<SkeletonRegionProps> = ({ children, label, className }) => {
  const { t } = useTranslation();
  return (
    <div
      className={`dms-skeleton${className ? ` ${className}` : ''}`}
      role="status"
      aria-busy="true"
      aria-label={label || t('common.loading', { defaultValue: 'Loading' })}
    >
      {children}
    </div>
  );
};

export interface SkeletonRowProps {
  /** Number of rows to render. */
  count?: number;
  /** Show a leading circular icon/avatar placeholder. */
  withIcon?: boolean;
  /** Show a right-aligned trailing value column (e.g. a timestamp/duration). */
  withEnd?: boolean;
}

/** One list/table row shape: icon + two stacked lines + trailing value column. */
export const SkeletonRow: React.FC<SkeletonRowProps> = ({ count = 1, withIcon = true, withEnd = true }) => (
  <>
    {Array.from({ length: count }).map((_, index) => (
      <div className="dms-skeleton-row" key={index}>
        {withIcon ? <SkeletonBlock circle height="1.25rem" /> : <span />}
        <div className="dms-skeleton-row__main">
          <SkeletonBlock width="55%" height="0.9rem" />
          <SkeletonBlock width="35%" height="0.7rem" />
        </div>
        {withEnd ? (
          <div className="dms-skeleton-row__end">
            <SkeletonBlock width="4.5rem" height="0.8rem" />
            <SkeletonBlock width="3rem" height="0.65rem" />
          </div>
        ) : null}
      </div>
    ))}
  </>
);

export interface SkeletonTableProps {
  /** Relative width of each column, e.g. [1, 2, 1, 1]. */
  columns: number[];
  rows?: number;
  showHeader?: boolean;
}

/** A shaped table: header bar + N rows, each cell width-matched to a real column. */
export const SkeletonTable: React.FC<SkeletonTableProps> = ({ columns, rows = 6, showHeader = true }) => (
  <div>
    {showHeader ? (
      <div className="dms-skeleton-table__header">
        {columns.map((flex, index) => (
          <SkeletonBlock key={index} height="0.75rem" style={{ flex }} />
        ))}
      </div>
    ) : null}
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div className="dms-skeleton-table__row" key={rowIndex}>
        {columns.map((flex, colIndex) => (
          <SkeletonBlock key={colIndex} height="0.85rem" width={colIndex === 0 ? '70%' : '85%'} style={{ flex }} />
        ))}
      </div>
    ))}
  </div>
);

export interface SkeletonTreeProps {
  rows?: number;
  columns: number[];
}

/** A tree-table row shape: indent + expander chevron + width-matched columns. */
export const SkeletonTree: React.FC<SkeletonTreeProps> = ({ rows = 6, columns }) => (
  <div>
    {Array.from({ length: rows }).map((_, index) => (
      <div className="dms-skeleton-tree-row" key={index} style={{ paddingInlineStart: `${0.25 + (index % 3) * 0.9}rem` }}>
        <SkeletonBlock width="0.7rem" height="0.7rem" />
        {columns.map((flex, colIndex) => (
          <SkeletonBlock key={colIndex} height="0.8rem" width={colIndex === 0 ? '60%' : '80%'} style={{ flex }} />
        ))}
      </div>
    ))}
  </div>
);

export interface SkeletonKpiRailProps {
  rows?: number;
}

/** A dashboard KPI/summary rail: label+percent line above a thin progress bar. */
export const SkeletonKpiRail: React.FC<SkeletonKpiRailProps> = ({ rows = 3 }) => (
  <div>
    {Array.from({ length: rows }).map((_, index) => (
      <div className="dms-skeleton-kpi-row" key={index}>
        <div className="dms-skeleton-kpi-row__labels">
          <SkeletonBlock width="40%" height="0.8rem" />
          <SkeletonBlock width="15%" height="0.8rem" />
        </div>
        <SkeletonBlock width="100%" height="0.5rem" />
      </div>
    ))}
  </div>
);

/** A chart-shaped placeholder: use for any bar/line chart panel. */
export const SkeletonChartPanel: React.FC<{ height?: number | string }> = ({ height = '14rem' }) => (
  <SkeletonBlock width="100%" height={height} borderRadius={8} />
);

export interface SkeletonFieldsetProps {
  /** Rows per fieldset group, one entry per group. */
  groups: number[];
}

/** Matches the Retention-style bordered fieldset/legend layout. */
export const SkeletonFieldsetGroup: React.FC<SkeletonFieldsetProps> = ({ groups }) => (
  <div>
    {groups.map((rowCount, groupIndex) => (
      <div className="dms-skeleton-fieldset" key={groupIndex}>
        <SkeletonBlock className="dms-skeleton-fieldset__legend" width="7rem" height="0.75rem" />
        {Array.from({ length: rowCount }).map((_, rowIndex) => (
          <div className="dms-skeleton-fieldset__row" key={rowIndex}>
            <div className="dms-skeleton-row__main" style={{ flex: 1 }}>
              <SkeletonBlock width="45%" height="0.85rem" />
              <SkeletonBlock width="65%" height="0.7rem" />
            </div>
            <SkeletonBlock width="6rem" height="1.6rem" borderRadius={6} />
          </div>
        ))}
      </div>
    ))}
  </div>
);

/** Matches the ActionsWorkbench-style detail panel: header, meta grid, target rows. */
export const SkeletonDetailPanel: React.FC = () => (
  <div>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
      <SkeletonBlock width="55%" height="1.1rem" />
      <SkeletonBlock width="4.5rem" height="1.4rem" borderRadius={999} />
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.5rem', margin: '0.6rem 0' }}>
      {Array.from({ length: 3 }).map((_, index) => (
        <SkeletonBlock key={index} height="2.6rem" borderRadius={7} />
      ))}
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.5rem', marginBottom: '0.75rem' }}>
      {Array.from({ length: 4 }).map((_, index) => (
        <SkeletonBlock key={index} height="2.2rem" borderRadius={7} />
      ))}
    </div>
    <SkeletonBlock width="35%" height="0.85rem" style={{ marginBottom: '0.5rem' }} />
    <SkeletonRow count={2} />
  </div>
);
